import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { salesAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { theme } from '../../theme';

const _DELETE_ROLES = ['ceo', 'cfo', 'cto', 'super_admin', 'sales_head'];

const TODAY = new Date().toISOString().slice(0, 10);

const INDIAN_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
  'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
  'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
  'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
  'Andaman & Nicobar Islands', 'Chandigarh', 'Dadra & Nagar Haveli and Daman & Diu',
  'Delhi', 'Jammu & Kashmir', 'Ladakh', 'Lakshadweep', 'Puducherry',
];

const OPP_STAGES = [
  'identified', 'interaction', 'proposal', 'technically_qualified',
  'won', 'lost', 'dropped', 'cancelled',
];

const APPROVAL_STAGE_STYLE = {
  draft:       { background: theme.pageBg,   color: theme.textMuted },
  pending_cfo: { background: '#FEF3C7',      color: '#92400E' },
  pending_cto: { background: '#DBEAFE',      color: '#1D4ED8' },
  pending_ceo: { background: '#F3E8FF',      color: '#7C3AED' },
  approved:    { background: '#D1FAE5',      color: '#065F46' },
  rejected:    { background: '#FEE2E2',      color: '#991B1B' },
};

const APPROVAL_STAGE_LABEL = {
  draft:       'Draft',
  pending_cfo: 'Pending CFO',
  pending_cto: 'Pending CTO',
  pending_ceo: 'Pending CEO',
  approved:    'Approved',
  rejected:    'Rejected',
};

const INPUT = {
  border: `1px solid ${theme.border}`, borderRadius: '0.5rem',
  padding: '0.5rem 0.75rem', fontSize: '0.875rem',
  color: theme.text, background: '#fff', outline: 'none',
  boxSizing: 'border-box', width: '100%',
};

const LABEL = {
  display: 'block', fontSize: '0.75rem', fontWeight: 500,
  color: theme.textMuted, marginBottom: '0.25rem',
};

const EMPTY_PQ = () => ({ condition: '', documents: [''], status: 'eligible' });
const EMPTY_TQ = () => ({ description: '', scoring_rows: [{ scoring: '', max_marks: '', actual_marks: '', remarks: '' }] });
const EMPTY_SCORING_ROW = () => ({ scoring: '', max_marks: '', actual_marks: '', remarks: '' });

const getPQStatus = (pq_criteria) => {
  if (!pq_criteria || pq_criteria.length === 0) return null;
  return pq_criteria.every(p => p.status === 'eligible') ? 'eligible' : 'not_eligible';
};

const getTQStatus = (tq_criteria, min_required_marks) => {
  if (!tq_criteria || tq_criteria.length === 0) return null;
  const total = tq_criteria.reduce((s, t) => s + (t.scoring_rows || []).reduce((ss, r) => ss + (parseFloat(r.actual_marks) || 0), 0), 0);
  if (min_required_marks == null) return null;
  return total >= parseFloat(min_required_marks) ? 'eligible' : 'not_eligible';
};

const StatusBadge = ({ status }) => {
  if (!status) return <span style={{ color: '#9CA3AF', fontSize: '0.75rem' }}>N/A</span>;
  const eligible = status === 'eligible';
  return (
    <span style={{ fontSize: '0.72rem', fontWeight: 700, padding: '0.2rem 0.6rem', borderRadius: '999px', whiteSpace: 'nowrap',
      background: eligible ? '#D1FAE5' : '#FEE2E2', color: eligible ? '#065F46' : '#991B1B' }}>
      {eligible ? 'Eligible' : 'Not Eligible'}
    </span>
  );
};

const EMPTY_FORM = {
  // Opportunity Details
  tender_id_ref: '', account_type: '', account_name: '', department_name: '',
  ministry: '', state: '', description: '', opportunity_type: '', opp_stage: 'identified',
  tender_release_date: '', submission_deadline: '', tentative_closure_date: '', emd_amount: '',
  selection_method: '', estimated_value: '', pq_eligible: 'no', tq_eligible: 'no',
  consortium_needed: 'no', competitors: [], win_probability: 50,
  // Client Info
  client_spoc_name: '', client_spoc_contact: '',
  decision_maker_name: '', decision_maker_contact: '', client_remarks: '',
  // PQ / TQ
  pq_criteria: [], tq_criteria: [], min_required_marks: '',
  tq_available: 'no',
};

export default function TenderList() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [tenders, setTenders] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [stageFilter, setStageFilter] = useState(searchParams.get('stage') || '');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [competitorInput, setCompetitorInput] = useState('');
  const [saving, setSaving] = useState(false);

  const canDeleteTender = (t) =>
    _DELETE_ROLES.includes(user?.role) || Number(t.created_by) === Number(user?.id);

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (search) params.search = search;
    if (stageFilter) params.status = stageFilter;
    salesAPI.getTenders(params)
      .then(r => { setTenders(r.data.tenders || []); setTotal(r.data.total || 0); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [search, stageFilter]);

  useEffect(() => { load(); }, [load]);

  const f = (key, val) => setForm(p => ({ ...p, [key]: val }));

  const addCompetitor = () => {
    const v = competitorInput.trim();
    if (!v) return;
    setForm(p => ({ ...p, competitors: [...p.competitors, v] }));
    setCompetitorInput('');
  };

  const removeCompetitor = (i) =>
    setForm(p => ({ ...p, competitors: p.competitors.filter((_, idx) => idx !== i) }));

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.account_name.trim())      { alert('Account Name is required'); return; }
    if (!form.submission_deadline)      { alert('Bid Submission Date is required'); return; }
    if (!form.client_spoc_name.trim())  { alert('Client SPOC Name is required'); return; }
    if (!form.client_spoc_contact.trim()){ alert('Client SPOC Contact is required'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        emd_amount:      form.emd_amount      !== '' ? parseFloat(form.emd_amount)      : null,
        estimated_value: form.estimated_value  !== '' ? parseFloat(form.estimated_value) : null,
        win_probability: parseInt(form.win_probability) || 50,
        pq_eligible: getPQStatus(form.pq_criteria) === 'eligible',
        tq_eligible: getTQStatus(form.tq_criteria, form.min_required_marks) === 'eligible',
        consortium_needed: form.consortium_needed  === 'yes',
        tender_release_date:    form.tender_release_date    || null,
        submission_deadline:    form.submission_deadline    || null,
        tentative_closure_date: form.tentative_closure_date || null,
        min_required_marks: form.min_required_marks !== '' ? parseInt(form.min_required_marks) : null,
        pq_criteria: form.pq_criteria.map((p, i) => ({
          ...p, pq_number: `PQ-${i + 1}`,
          documents: p.documents.filter(d => d.trim()),
        })),
        tq_criteria: form.tq_criteria.map((t, i) => ({
          tq_number: `TQ-${i + 1}`,
          description: t.description || '',
          scoring_rows: (t.scoring_rows || []).map(r => ({
            scoring:      r.scoring      || '',
            max_marks:    parseFloat(r.max_marks)    || 0,
            actual_marks: parseFloat(r.actual_marks) || 0,
            remarks:      r.remarks      || '',
          })),
        })),
      };
      const r = await salesAPI.createTender(payload);
      navigate(`/sales/tenders/${r.data.id}`);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to create opportunity');
    } finally {
      setSaving(false);
    }
  };

  const closeForm = () => { setShowForm(false); setForm(EMPTY_FORM); setCompetitorInput(''); };

  const handleDelete = async (e, t) => {
    e.stopPropagation();
    if (!window.confirm(`Delete "${t.account_name || t.title}"? This cannot be undone.`)) return;
    try {
      await salesAPI.deleteTender(t.id);
      setTenders(prev => prev.filter(x => x.id !== t.id));
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete.');
    }
  };

  // ── PQ helpers ──
  const addPQ    = () => setForm(p => ({ ...p, pq_criteria: [...p.pq_criteria, EMPTY_PQ()] }));
  const removePQ = (i) => setForm(p => ({ ...p, pq_criteria: p.pq_criteria.filter((_, idx) => idx !== i) }));
  const setPQ    = (i, key, val) => setForm(p => {
    const rows = [...p.pq_criteria]; rows[i] = { ...rows[i], [key]: val }; return { ...p, pq_criteria: rows };
  });
  const addPQDoc    = (i) => setForm(p => { const rows = [...p.pq_criteria]; rows[i] = { ...rows[i], documents: [...rows[i].documents, ''] }; return { ...p, pq_criteria: rows }; });
  const setPQDoc    = (i, di, val) => setForm(p => { const rows = [...p.pq_criteria]; const docs = [...rows[i].documents]; docs[di] = val; rows[i] = { ...rows[i], documents: docs }; return { ...p, pq_criteria: rows }; });
  const removePQDoc = (i, di) => setForm(p => { const rows = [...p.pq_criteria]; rows[i] = { ...rows[i], documents: rows[i].documents.filter((_, idx) => idx !== di) }; return { ...p, pq_criteria: rows }; });

  // ── TQ helpers ──
  const addTQ    = () => setForm(p => ({ ...p, tq_criteria: [...p.tq_criteria, EMPTY_TQ()] }));
  const removeTQ = (i) => setForm(p => ({ ...p, tq_criteria: p.tq_criteria.filter((_, idx) => idx !== i) }));
  const updateTQDesc = (i, val) => setForm(p => {
    const rows = [...p.tq_criteria]; rows[i] = { ...rows[i], description: val }; return { ...p, tq_criteria: rows };
  });
  const addScoringRow = (i) => setForm(p => {
    const rows = [...p.tq_criteria]; rows[i] = { ...rows[i], scoring_rows: [...(rows[i].scoring_rows || []), EMPTY_SCORING_ROW()] }; return { ...p, tq_criteria: rows };
  });
  const updateScoringRow = (i, j, patch) => setForm(p => {
    const rows = [...p.tq_criteria]; const sr = [...(rows[i].scoring_rows || [])]; sr[j] = { ...sr[j], ...patch }; rows[i] = { ...rows[i], scoring_rows: sr }; return { ...p, tq_criteria: rows };
  });
  const removeScoringRow = (i, j) => setForm(p => {
    const rows = [...p.tq_criteria]; rows[i] = { ...rows[i], scoring_rows: rows[i].scoring_rows.filter((_, idx) => idx !== j) }; return { ...p, tq_criteria: rows };
  });

  // ── Derived PQ/TQ status ──
  const pqOverallEligible = form.pq_criteria.length > 0 && form.pq_criteria.every(r => r.status === 'eligible');
  const tqTotalMax    = form.tq_criteria.reduce((s, t) => s + (t.scoring_rows || []).reduce((ss, r) => ss + (parseFloat(r.max_marks) || 0), 0), 0);
  const tqTotalActual = form.tq_criteria.reduce((s, t) => s + (t.scoring_rows || []).reduce((ss, r) => ss + (parseFloat(r.actual_marks) || 0), 0), 0);
  const tqEligible    = form.tq_criteria.length > 0 && form.min_required_marks !== '' && tqTotalActual >= parseFloat(form.min_required_marks);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: theme.text, margin: 0 }}>Tenders & Bids</h1>
          <p style={{ color: theme.textMuted, fontSize: '0.875rem', margin: '0.25rem 0 0' }}>{total} opportunit{total !== 1 ? 'ies' : 'y'}</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          style={{ background: theme.primary, color: '#fff', border: 'none', borderRadius: '0.5rem', padding: '0.5rem 1rem', fontSize: '0.875rem', fontWeight: 500, cursor: 'pointer' }}
        >
          + New Opportunity
        </button>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'center' }}>
        <input
          type="text" placeholder="Search…" value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ ...INPUT, width: '16rem' }}
        />
        <select value={stageFilter} onChange={e => setStageFilter(e.target.value)} style={{ ...INPUT, width: 'auto' }}>
          <option value="">All Stages</option>
          {OPP_STAGES.map(s => (
            <option key={s} value={s}>{s.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>
          ))}
        </select>
        {(search || stageFilter) && (
          <button onClick={() => { setSearch(''); setStageFilter(''); }}
            style={{ background: 'none', border: 'none', fontSize: '0.875rem', color: theme.textMuted, cursor: 'pointer', textDecoration: 'underline' }}>
            Clear
          </button>
        )}
      </div>

      {/* Table */}
      <div style={{ background: theme.cardBg, borderRadius: '0.75rem', border: `1px solid ${theme.border}`, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: theme.textMuted }}>Loading…</div>
        ) : tenders.length === 0 ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: theme.textMuted }}>No opportunities found</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr style={{ background: theme.pageBg, borderBottom: `1px solid ${theme.border}` }}>
                  {['BD ID', 'Account Name', 'Account Type', 'Opportunity Type', 'Stage', 'Est. Value', 'Bid Submission', 'Closure Date', 'PQ Status', 'TQ Status', 'Created By', 'Assigned To', 'Approval', ''].map(h => (
                    <th key={h} style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.7rem', fontWeight: 600, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {tenders.map((t, i) => (
                  <tr
                    key={t.id}
                    onClick={() => navigate(`/sales/tenders/${t.id}`)}
                    style={{ borderBottom: `1px solid ${theme.border}`, cursor: 'pointer', background: i % 2 === 0 ? '#fff' : theme.pageBg }}
                    onMouseEnter={e => e.currentTarget.style.background = theme.lightBg}
                    onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? '#fff' : theme.pageBg}
                  >
                    <td style={{ padding: '0.75rem 1rem', fontFamily: 'monospace', fontSize: '0.75rem', color: theme.textMuted, whiteSpace: 'nowrap' }}>{t.bd_id || t.reference_number}</td>
                    <td style={{ padding: '0.75rem 1rem', fontWeight: 500, color: theme.text, maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {t.account_name || t.title}
                    </td>
                    <td style={{ padding: '0.75rem 1rem', color: theme.textMuted }}>{t.account_type || '—'}</td>
                    <td style={{ padding: '0.75rem 1rem', color: theme.textMuted, maxWidth: '140px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {t.opportunity_type || '—'}
                    </td>
                    <td style={{ padding: '0.75rem 1rem' }}>
                      <span style={{ background: '#EEF2FF', color: '#4338CA', padding: '0.2rem 0.6rem', borderRadius: '999px', fontSize: '0.72rem', fontWeight: 500, textTransform: 'capitalize', whiteSpace: 'nowrap' }}>
                        {(t.opp_stage || 'identified').replace('_', ' ')}
                      </span>
                    </td>
                    <td style={{ padding: '0.75rem 1rem', color: theme.text, fontWeight: 500, whiteSpace: 'nowrap' }}>
                      {t.estimated_value ? `₹${Number(t.estimated_value).toLocaleString()}` : '—'}
                    </td>
                    <td style={{ padding: '0.75rem 1rem', color: theme.textMuted, whiteSpace: 'nowrap' }}>
                      {t.submission_deadline ? new Date(t.submission_deadline).toLocaleDateString('en-IN') : '—'}
                    </td>
                    <td style={{ padding: '0.75rem 1rem', color: theme.textMuted, whiteSpace: 'nowrap' }}>
                      {t.tentative_closure_date ? new Date(t.tentative_closure_date).toLocaleDateString('en-IN') : '—'}
                    </td>
                    <td style={{ padding: '0.75rem 1rem' }}>
                      <StatusBadge status={getPQStatus(t.pq_criteria)} />
                    </td>
                    <td style={{ padding: '0.75rem 1rem' }}>
                      <StatusBadge status={getTQStatus(t.tq_criteria, t.min_required_marks)} />
                    </td>
                    <td style={{ padding: '0.75rem 1rem', fontSize: '0.8rem', color: theme.textMuted, whiteSpace: 'nowrap' }}>
                      {t.creator_name || '—'}
                    </td>
                    <td style={{ padding: '0.75rem 1rem', fontSize: '0.8rem', color: theme.textMuted, whiteSpace: 'nowrap' }}>
                      {t.assignee_name || '—'}
                    </td>
                    <td style={{ padding: '0.75rem 1rem' }}>
                      <span style={{ ...(APPROVAL_STAGE_STYLE[t.approval_stage] || APPROVAL_STAGE_STYLE.draft), padding: '0.2rem 0.6rem', borderRadius: '999px', fontSize: '0.72rem', fontWeight: 500, whiteSpace: 'nowrap' }}>
                        {APPROVAL_STAGE_LABEL[t.approval_stage] || 'Draft'}
                      </span>
                    </td>
                    <td style={{ padding: '0.75rem 1rem' }} onClick={e => e.stopPropagation()}>
                      {canDeleteTender(t) && (
                        <button
                          onClick={(e) => handleDelete(e, t)}
                          style={{ padding: '0.25rem 0.6rem', fontSize: '0.72rem', fontWeight: 600, border: '1px solid #FECACA', borderRadius: '0.375rem', background: '#fff', color: '#DC2626', cursor: 'pointer' }}
                          onMouseEnter={e => { e.currentTarget.style.background = '#FEE2E2'; }}
                          onMouseLeave={e => { e.currentTarget.style.background = '#fff'; }}
                          title="Delete"
                        >
                          Delete
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 50, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '2rem 1rem', overflowY: 'auto' }}>
          <div style={{ background: '#fff', borderRadius: '1rem', boxShadow: '0 20px 60px rgba(0,0,0,0.2)', width: '100%', maxWidth: '760px' }}>
            {/* Modal Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1rem 1.5rem', borderBottom: `1px solid ${theme.border}` }}>
              <h2 style={{ fontSize: '1.1rem', fontWeight: 700, color: theme.text, margin: 0 }}>New Opportunity</h2>
              <button onClick={closeForm} style={{ background: 'none', border: 'none', fontSize: '1.5rem', color: theme.textMuted, cursor: 'pointer', lineHeight: 1 }}>×</button>
            </div>

            <form onSubmit={handleCreate} style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>

              {/* ── Section 1: Opportunity Details ── */}
              <div>
                <h3 style={{ fontSize: '0.875rem', fontWeight: 700, color: theme.primary, margin: '0 0 1rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  Opportunity Details
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>

                  {/* BD ID — auto, read-only */}
                  <div>
                    <label style={LABEL}>BD ID (Auto Generated)</label>
                    <input disabled value="Auto-generated on save" style={{ ...INPUT, background: theme.pageBg, color: theme.textMuted }} />
                  </div>

                  {/* Tender ID */}
                  <div>
                    <label style={LABEL}>Tender ID</label>
                    <input value={form.tender_id_ref} onChange={e => f('tender_id_ref', e.target.value)} style={INPUT} placeholder="Official Tender / RFP ID" />
                  </div>

                  {/* Account Type */}
                  <div>
                    <label style={LABEL}>Account Type *</label>
                    <select required value={form.account_type} onChange={e => f('account_type', e.target.value)} style={INPUT}>
                      <option value="">Select…</option>
                      {['Govt', 'Enterprises', 'Banking'].map(v => <option key={v} value={v}>{v}</option>)}
                    </select>
                  </div>

                  {/* Account Name */}
                  <div>
                    <label style={LABEL}>Account Name *</label>
                    <input required value={form.account_name} onChange={e => f('account_name', e.target.value)} style={INPUT} placeholder="e.g. Tamil Nadu Government" />
                  </div>

                  {/* Department Name */}
                  <div>
                    <label style={LABEL}>Department Name</label>
                    <input value={form.department_name} onChange={e => f('department_name', e.target.value)} style={INPUT} />
                  </div>

                  {/* Ministry */}
                  <div>
                    <label style={LABEL}>Ministry</label>
                    <input value={form.ministry} onChange={e => f('ministry', e.target.value)} style={INPUT} />
                  </div>

                  {/* State */}
                  <div>
                    <label style={LABEL}>State</label>
                    <select value={form.state} onChange={e => f('state', e.target.value)} style={INPUT}>
                      <option value="">Select State / UT…</option>
                      {INDIAN_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>

                  {/* Opportunity Type */}
                  <div>
                    <label style={LABEL}>Opportunity Type *</label>
                    <select required value={form.opportunity_type} onChange={e => f('opportunity_type', e.target.value)} style={INPUT}>
                      <option value="">Select…</option>
                      {['Reactive RFP', 'Reactive RFQ', 'Reactive EOI', 'Proactive RFP', 'Proactive RFQ', 'Proactive EOI'].map(v => (
                        <option key={v} value={v}>{v}</option>
                      ))}
                    </select>
                  </div>

                  {/* Opportunity Description — full width */}
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={LABEL}>Opportunity Description</label>
                    <textarea rows={3} value={form.description} onChange={e => f('description', e.target.value)}
                      style={{ ...INPUT, resize: 'vertical' }} />
                  </div>

                  {/* Stage */}
                  <div>
                    <label style={LABEL}>Stage *</label>
                    <select required value={form.opp_stage} onChange={e => f('opp_stage', e.target.value)} style={INPUT}>
                      {OPP_STAGES.map(s => (
                        <option key={s} value={s}>{s.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>
                      ))}
                    </select>
                  </div>

                  {/* Opportunity Date — auto, read-only */}
                  <div>
                    <label style={LABEL}>Opportunity Date (Auto)</label>
                    <input disabled value={new Date().toLocaleDateString('en-IN')} style={{ ...INPUT, background: theme.pageBg, color: theme.textMuted }} />
                  </div>

                  {/* Tender Release Date — no future dates */}
                  <div>
                    <label style={LABEL}>Tender Release Date</label>
                    <input type="date" max={TODAY} value={form.tender_release_date} onChange={e => f('tender_release_date', e.target.value)} style={INPUT} />
                  </div>

                  {/* Bid Submission Date — mandatory */}
                  <div>
                    <label style={LABEL}>Bid Submission Date *</label>
                    <input required type="date" value={form.submission_deadline} onChange={e => f('submission_deadline', e.target.value)} style={INPUT} />
                  </div>

                  {/* Tentative Closure Date */}
                  <div>
                    <label style={LABEL}>Tentative Closure Date</label>
                    <input type="date" value={form.tentative_closure_date} onChange={e => f('tentative_closure_date', e.target.value)} style={INPUT} />
                  </div>

                  {/* EMD */}
                  <div>
                    <label style={LABEL}>EMD (₹)</label>
                    <input type="number" min="0" value={form.emd_amount} onChange={e => f('emd_amount', e.target.value)} style={INPUT} />
                  </div>

                  {/* Selection Method */}
                  <div>
                    <label style={LABEL}>Selection Method</label>
                    <select value={form.selection_method} onChange={e => f('selection_method', e.target.value)} style={INPUT}>
                      <option value="">Select…</option>
                      {['L1', 'QCBS', 'Nomination'].map(v => <option key={v} value={v}>{v}</option>)}
                    </select>
                  </div>

                  {/* Estimated Value */}
                  <div>
                    <label style={LABEL}>Estimated Value (₹)</label>
                    <input type="number" min="0" value={form.estimated_value} onChange={e => f('estimated_value', e.target.value)} style={INPUT} />
                  </div>

                  {/* PQ Eligible */}
                  <div>
                    <label style={LABEL}>PQ Eligible (Auto)</label>
                    <div style={{ ...INPUT, background: theme.pageBg, display: 'flex', alignItems: 'center', height: '36px' }}>
                      <StatusBadge status={getPQStatus(form.pq_criteria)} />
                      {!getPQStatus(form.pq_criteria) && <span style={{ fontSize: '0.78rem', color: theme.textMuted }}>Derived from PQ criteria</span>}
                    </div>
                  </div>

                  <div>
                    <label style={LABEL}>TQ Eligible (Auto)</label>
                    <div style={{ ...INPUT, background: theme.pageBg, display: 'flex', alignItems: 'center', height: '36px' }}>
                      <StatusBadge status={getTQStatus(form.tq_criteria, form.min_required_marks)} />
                      {!getTQStatus(form.tq_criteria, form.min_required_marks) && <span style={{ fontSize: '0.78rem', color: theme.textMuted }}>Derived from TQ criteria</span>}
                    </div>
                  </div>

                  {/* Consortium Needed */}
                  <div>
                    <label style={LABEL}>Consortium Needed</label>
                    <select value={form.consortium_needed} onChange={e => f('consortium_needed', e.target.value)} style={INPUT}>
                      <option value="yes">Yes</option>
                      <option value="no">No</option>
                    </select>
                  </div>

                  {/* Win Probability */}
                  <div>
                    <label style={LABEL}>Win Probability (%)</label>
                    <input type="number" min="0" max="100" value={form.win_probability} onChange={e => f('win_probability', e.target.value)} style={INPUT} />
                  </div>

                  {/* Competitors — full width */}
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={LABEL}>Competitors</label>
                    <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                      <input
                        value={competitorInput}
                        onChange={e => setCompetitorInput(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addCompetitor(); } }}
                        placeholder="Type name and press Enter or Add"
                        style={{ ...INPUT, flex: 1 }}
                      />
                      <button type="button" onClick={addCompetitor}
                        style={{ padding: '0.5rem 0.875rem', fontSize: '0.875rem', background: theme.primary, color: '#fff', border: 'none', borderRadius: '0.5rem', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                        Add
                      </button>
                    </div>
                    {form.competitors.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                        {form.competitors.map((c, i) => (
                          <span key={i} style={{ background: '#EEF2FF', color: '#4338CA', padding: '0.2rem 0.6rem', borderRadius: '999px', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                            {c}
                            <button type="button" onClick={() => removeCompetitor(i)}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#4338CA', fontWeight: 700, lineHeight: 1, padding: 0, fontSize: '0.875rem' }}>×</button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                </div>
              </div>

              {/* ── Section 2: Client Info ── */}
              <div>
                <h3 style={{ fontSize: '0.875rem', fontWeight: 700, color: theme.primary, margin: '0 0 1rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  Client Info
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>

                  <div>
                    <label style={LABEL}>Client SPOC Name *</label>
                    <input required value={form.client_spoc_name} onChange={e => f('client_spoc_name', e.target.value)} style={INPUT} />
                  </div>

                  <div>
                    <label style={LABEL}>Client SPOC Contact *</label>
                    <input required value={form.client_spoc_contact} onChange={e => f('client_spoc_contact', e.target.value)} style={INPUT} placeholder="Phone / Email" />
                  </div>

                  <div>
                    <label style={LABEL}>Decision Maker Name *</label>
                    <input required value={form.decision_maker_name} onChange={e => f('decision_maker_name', e.target.value)} style={INPUT} />
                  </div>

                  <div>
                    <label style={LABEL}>Decision Maker Contact *</label>
                    <input required value={form.decision_maker_contact} onChange={e => f('decision_maker_contact', e.target.value)} style={INPUT} placeholder="Phone / Email" />
                  </div>

                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={LABEL}>Remarks</label>
                    <textarea rows={2} value={form.client_remarks} onChange={e => f('client_remarks', e.target.value)}
                      style={{ ...INPUT, resize: 'vertical' }} />
                  </div>

                </div>
              </div>

              {/* ── Section 3: Pre-Qualification Criteria (PQ) ── */}
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <h3 style={{ fontSize: '0.875rem', fontWeight: 700, color: theme.primary, margin: 0, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    Pre-Qualification Criteria (PQ)
                  </h3>
                  {form.pq_criteria.length > 0 && (
                    <span style={{ fontSize: '0.78rem', fontWeight: 700, padding: '0.2rem 0.75rem', borderRadius: '999px', background: pqOverallEligible ? '#D1FAE5' : '#FEE2E2', color: pqOverallEligible ? '#065F46' : '#991B1B' }}>
                      Overall PQ: {pqOverallEligible ? 'Eligible ✓' : 'Not Eligible ✗'}
                    </span>
                  )}
                </div>

                {form.pq_criteria.length === 0 && (
                  <p style={{ fontSize: '0.82rem', color: theme.textMuted, marginBottom: '0.75rem' }}>No PQ criteria added. Click "+ Add PQ" to add.</p>
                )}

                {form.pq_criteria.map((pq, i) => (
                  <div key={i} style={{ border: `1px solid ${theme.border}`, borderRadius: '0.5rem', padding: '0.875rem', marginBottom: '0.75rem', background: theme.pageBg, position: 'relative' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.6rem' }}>
                      <span style={{ fontSize: '0.78rem', fontWeight: 700, color: theme.primary }}>PQ-{i + 1}</span>
                      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                        <select value={pq.status} onChange={e => setPQ(i, 'status', e.target.value)}
                          style={{ ...INPUT, width: 'auto', fontSize: '0.78rem', padding: '0.25rem 0.5rem',
                            background: pq.status === 'eligible' ? '#D1FAE5' : '#FEE2E2',
                            color: pq.status === 'eligible' ? '#065F46' : '#991B1B',
                            fontWeight: 700, border: 'none' }}>
                          <option value="eligible">Eligible</option>
                          <option value="not_eligible">Not Eligible</option>
                        </select>
                        <button type="button" onClick={() => removePQ(i)}
                          style={{ background: 'none', border: 'none', color: '#EF4444', cursor: 'pointer', fontSize: '1.1rem', lineHeight: 1, padding: '0.1rem 0.3rem' }}>×</button>
                      </div>
                    </div>

                    <label style={LABEL}>Eligibility Condition *</label>
                    <input value={pq.condition} onChange={e => setPQ(i, 'condition', e.target.value)}
                      style={{ ...INPUT, marginBottom: '0.6rem' }} placeholder="e.g. Must have 5 years experience in IT infrastructure..." />

                    <label style={LABEL}>Document Proofs</label>
                    {pq.documents.map((doc, di) => (
                      <div key={di} style={{ display: 'flex', gap: '0.4rem', marginBottom: '0.35rem' }}>
                        <input value={doc} onChange={e => setPQDoc(i, di, e.target.value)}
                          style={{ ...INPUT, flex: 1 }} placeholder={`Document proof ${di + 1}`} />
                        {pq.documents.length > 1 && (
                          <button type="button" onClick={() => removePQDoc(i, di)}
                            style={{ background: 'none', border: `1px solid ${theme.border}`, borderRadius: '0.375rem', color: theme.textMuted, cursor: 'pointer', padding: '0.25rem 0.5rem', fontSize: '0.9rem' }}>×</button>
                        )}
                      </div>
                    ))}
                    <button type="button" onClick={() => addPQDoc(i)}
                      style={{ background: 'none', border: 'none', color: theme.primary, cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600, padding: 0 }}>
                      + Add document
                    </button>
                  </div>
                ))}

                <button type="button" onClick={addPQ}
                  style={{ padding: '0.4rem 1rem', background: '#EEF2FF', color: '#4338CA', border: '1px dashed #A5B4FC', borderRadius: '0.5rem', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600 }}>
                  + Add PQ Criteria
                </button>
              </div>

              {/* ── Section 4: Technical Qualification Criteria (TQ) ── */}
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <h3 style={{ fontSize: '0.875rem', fontWeight: 700, color: theme.primary, margin: 0, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    Technical Qualification Criteria (TQ)
                  </h3>
                  {form.tq_available === 'yes' && form.tq_criteria.length > 0 && form.min_required_marks !== '' && (
                    <span style={{ fontSize: '0.78rem', fontWeight: 700, padding: '0.2rem 0.75rem', borderRadius: '999px', background: tqEligible ? '#D1FAE5' : '#FEE2E2', color: tqEligible ? '#065F46' : '#991B1B' }}>
                      TQ: {tqEligible ? 'Eligible ✓' : 'Not Eligible ✗'}
                    </span>
                  )}
                </div>

                {/* TQ Available toggle */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem', padding: '0.75rem', background: theme.pageBg, borderRadius: '0.5rem', border: `1px solid ${theme.border}` }}>
                  <label style={{ fontSize: '0.85rem', fontWeight: 600, color: theme.text, margin: 0 }}>TQ Criteria Available?</label>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    {['yes', 'no'].map(v => (
                      <button key={v} type="button"
                        onClick={() => { f('tq_available', v); if (v === 'no') { f('tq_criteria', []); f('min_required_marks', ''); } }}
                        style={{
                          padding: '0.35rem 1rem', fontSize: '0.82rem', fontWeight: 600, borderRadius: '0.4rem', cursor: 'pointer', border: 'none',
                          background: form.tq_available === v ? (v === 'yes' ? theme.primary : '#EF4444') : theme.border,
                          color: form.tq_available === v ? '#fff' : theme.textMuted,
                        }}>
                        {v === 'yes' ? 'Yes' : 'No'}
                      </button>
                    ))}
                  </div>
                </div>

                {form.tq_available === 'yes' && form.tq_criteria.length === 0 && (
                  <p style={{ fontSize: '0.82rem', color: theme.textMuted, marginBottom: '0.75rem' }}>No TQ criteria added. Click "+ Add TQ Criteria" to add.</p>
                )}

                {form.tq_available === 'yes' && form.tq_criteria.map((tq, i) => {
                  const rows = tq.scoring_rows || [];
                  const tqMax    = rows.reduce((s, r) => s + (parseFloat(r.max_marks) || 0), 0);
                  const tqActual = rows.reduce((s, r) => s + (parseFloat(r.actual_marks) || 0), 0);
                  return (
                    <div key={i} style={{ border: `1.5px solid ${theme.border}`, borderRadius: '0.625rem', marginBottom: '0.875rem', overflow: 'hidden' }}>
                      {/* TQ header */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem 0.875rem', background: theme.pageBg, borderBottom: `1px solid ${theme.border}` }}>
                        <span style={{ fontSize: '0.8rem', fontWeight: 700, color: theme.primary }}>TQ-{i + 1}</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                          {rows.length > 0 && <span style={{ fontSize: '0.72rem', color: theme.textMuted }}>Sub-total: {tqActual} / {tqMax}</span>}
                          <button type="button" onClick={() => removeTQ(i)}
                            style={{ background: 'none', border: 'none', color: '#EF4444', cursor: 'pointer', fontSize: '1rem', lineHeight: 1 }}>×</button>
                        </div>
                      </div>
                      {/* Criteria description */}
                      <div style={{ padding: '0.75rem 0.875rem', borderBottom: `1px solid ${theme.border}` }}>
                        <label style={LABEL}>Criteria Description *</label>
                        <input value={tq.description} onChange={e => updateTQDesc(i, e.target.value)}
                          style={{ ...INPUT, width: '100%' }} placeholder="e.g. Financial Capability" />
                      </div>
                      {/* Scoring rows table */}
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                          <thead>
                            <tr style={{ background: '#f9fafb' }}>
                              {['Scoring / Parameter', 'Max Marks', 'Actual Marks', 'Remarks (Optional)', ''].map((h, hi) => (
                                <th key={hi} style={{ padding: '0.4rem 0.625rem', textAlign: hi >= 1 && hi <= 2 ? 'right' : 'left', fontSize: '0.7rem', fontWeight: 600, color: theme.textMuted, borderBottom: `1px solid ${theme.border}`, whiteSpace: 'nowrap' }}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {rows.map((r, j) => (
                              <tr key={j} style={{ borderBottom: `1px solid ${theme.border}` }}>
                                <td style={{ padding: '0.4rem 0.625rem' }}>
                                  <input value={r.scoring || ''} onChange={e => updateScoringRow(i, j, { scoring: e.target.value })}
                                    style={{ ...INPUT, width: '100%', minWidth: 160 }} placeholder="e.g. Annual Turnover" />
                                </td>
                                <td style={{ padding: '0.4rem 0.625rem' }}>
                                  <input type="number" min="0" value={r.max_marks || ''} onChange={e => updateScoringRow(i, j, { max_marks: e.target.value })}
                                    style={{ ...INPUT, width: 90, textAlign: 'right' }} placeholder="0" />
                                </td>
                                <td style={{ padding: '0.4rem 0.625rem' }}>
                                  <input type="number" min="0" value={r.actual_marks || ''} onChange={e => updateScoringRow(i, j, { actual_marks: e.target.value })}
                                    style={{ ...INPUT, width: 90, textAlign: 'right' }} placeholder="0" />
                                </td>
                                <td style={{ padding: '0.4rem 0.625rem' }}>
                                  <input value={r.remarks || ''} onChange={e => updateScoringRow(i, j, { remarks: e.target.value })}
                                    style={{ ...INPUT, width: '100%', minWidth: 120 }} placeholder="Optional" />
                                </td>
                                <td style={{ padding: '0.4rem 0.625rem', textAlign: 'center' }}>
                                  {rows.length > 1 && (
                                    <button type="button" onClick={() => removeScoringRow(i, j)}
                                      style={{ background: 'none', border: 'none', color: '#EF4444', cursor: 'pointer', fontSize: '0.85rem', padding: '0 0.25rem' }}>×</button>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {/* Add scoring row */}
                      <div style={{ padding: '0.5rem 0.875rem' }}>
                        <button type="button" onClick={() => addScoringRow(i)}
                          style={{ padding: '0.3rem 0.875rem', background: '#EEF2FF', color: '#4338CA', border: '1px dashed #A5B4FC', borderRadius: '0.4rem', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600 }}>
                          + Add Scoring Row
                        </button>
                      </div>
                    </div>
                  );
                })}

                {form.tq_available === 'yes' && (
                  <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap', marginTop: '0.25rem' }}>
                    <button type="button" onClick={addTQ}
                      style={{ padding: '0.4rem 1rem', background: '#EEF2FF', color: '#4338CA', border: '1px dashed #A5B4FC', borderRadius: '0.5rem', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600 }}>
                      + Add TQ Criteria
                    </button>
                    {form.tq_criteria.length > 0 && (
                      <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <label style={{ ...LABEL, margin: 0, whiteSpace: 'nowrap' }}>Min Required Marks:</label>
                          <input type="number" min="0" value={form.min_required_marks}
                            onChange={e => f('min_required_marks', e.target.value)}
                            style={{ ...INPUT, width: '90px' }} placeholder="0" />
                        </div>
                        <div style={{ fontSize: '0.8rem', color: theme.textMuted, display: 'flex', gap: '1rem' }}>
                          <span>Total Max: <strong style={{ color: theme.text }}>{tqTotalMax}</strong></span>
                          <span>Total Actual: <strong style={{ color: theme.text }}>{tqTotalActual}</strong></span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', paddingTop: '0.25rem', borderTop: `1px solid ${theme.border}` }}>
                <button type="button" onClick={closeForm}
                  style={{ padding: '0.5rem 1.25rem', fontSize: '0.875rem', color: theme.textMuted, border: `1px solid ${theme.border}`, borderRadius: '0.5rem', background: '#fff', cursor: 'pointer' }}>
                  Cancel
                </button>
                <button type="submit" disabled={saving}
                  style={{ padding: '0.5rem 1.25rem', fontSize: '0.875rem', background: theme.primary, color: '#fff', border: 'none', borderRadius: '0.5rem', cursor: 'pointer', opacity: saving ? 0.6 : 1 }}>
                  {saving ? 'Creating…' : 'Create & Submit for Approval'}
                </button>
              </div>

            </form>
          </div>
        </div>
      )}
    </div>
  );
}
